import React, { useState, useEffect } from 'react';
import { quoteService } from '../api/quoteService';
import Table from '../components/Common/Table';
import Modal from '../components/Common/Modal';
import Pagination from '../components/Common/Pagination';
import SearchBar from '../components/Common/SearchBar';
import QuoteForm from '../components/Quotes/QuoteForm';
import { ShoppingCart, CreditCard } from 'lucide-react';
import '../components/Common/Common.css';

function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [viewQuote, setViewQuote] = useState(null);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertingQuote, setConvertingQuote] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [converting, setConverting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [convertedSale, setConvertedSale] = useState(null);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const response = await quoteService.getQuotes({
        page: currentPage,
        search: searchTerm,
        per_page: 15,
      });
      setQuotes(response.data);
      setLastPage(response.meta?.last_page || 1);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, [currentPage, searchTerm]);

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce devis ?')) {
      try {
        await quoteService.deleteQuote(id);
        fetchQuotes();
      } catch (error) {
        console.error('Error deleting quote:', error);
        alert('Échec de la suppression du devis');
      }
    }
  };

  const handleEdit = (quote) => {
    setEditingQuote(quote);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingQuote(null);
    setShowModal(true);
  };

  const handleView = async (quote) => {
    try {
      const fullQuote = await quoteService.getQuote(quote.id);
      setViewQuote(fullQuote);
    } catch (error) {
      console.error('Error fetching quote details:', error);
      alert('Échec du chargement des détails du devis');
    }
  };

  const handleDownloadPdf = async (quote) => {
    try {
      await quoteService.downloadQuotePdf(quote.id);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Échec du téléchargement du PDF');
    }
  };

  const handleFormSuccess = () => {
    setShowModal(false);
    setEditingQuote(null);
    fetchQuotes();
  };

  const handleConvertToSale = (quote) => {
    setConvertingQuote(quote);
    setPaymentMethod('cash');
    setShowConvertModal(true);
  };

  const confirmConvertToSale = async () => {
    if (!paymentMethod) {
      alert('Veuillez sélectionner une méthode de paiement');
      return;
    }

    try {
      setConverting(true);
      const response = await quoteService.convertToSale(convertingQuote.id, paymentMethod);
      setConvertedSale(response);
      setShowConvertModal(false);
      setShowSuccessModal(true);
      fetchQuotes();
    } catch (error) {
      console.error('Error converting quote to sale:', error);
      alert(error.response?.data?.message || 'Échec de la conversion du devis en vente');
    } finally {
      setConverting(false);
    }
  };

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'client_name', label: 'Nom du client' },
    { key: 'client_email', label: 'Email' },
    { key: 'client_phone', label: 'Téléphone' },
    { key: 'total', label: 'Total', render: (val) => `${parseFloat(val).toFixed(2)} DH` },
    {
      key: 'status',
      label: 'Statut',
      render: (val) => val === 'converted' ? (
        <span className="badge badge-success">Converti</span>
      ) : (
        <span className="badge badge-info">En attente</span>
      )
    },
    { key: 'created_at', label: 'Créé le', render: (val) => new Date(val).toLocaleDateString() },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>Devis</h1>
        <button onClick={handleCreate} className="btn btn-primary">
          Créer un devis
        </button>
      </div>

      <div className="filters">
        <div className="filters-grid">
          <div style={{ gridColumn: 'span 2' }}>
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Rechercher des devis par nom de client ou email..."
              onClear={() => setCurrentPage(1)}
            />
          </div>
        </div>
      </div>

      <Table
        columns={columns}
        data={quotes}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        actions={true}
        customActions={(quote) => (
          <>
            <button
              onClick={() => handleDownloadPdf(quote)}
              className="btn btn-small btn-success"
              style={{ marginLeft: '5px' }}
            >
              Télécharger PDF
            </button>
            {quote.status !== 'converted' && (
              <button
                onClick={() => handleConvertToSale(quote)}
                className="btn btn-small btn-info"
                style={{ marginLeft: '5px' }}
                title="Convertir en vente"
              >
                <ShoppingCart size={16} />
                Convertir
              </button>
            )}
          </>
        )}
      />

      <Pagination
        currentPage={currentPage}
        lastPage={lastPage}
        onPageChange={setCurrentPage}
      />

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingQuote ? 'Modifier le devis' : 'Créer un devis'}
        size="large"
      >
        <QuoteForm
          quote={editingQuote}
          onSuccess={handleFormSuccess}
          onCancel={() => setShowModal(false)}
        />
      </Modal>

      {/* View Quote Modal */}
      {viewQuote && (
        <Modal
          isOpen={!!viewQuote}
          onClose={() => setViewQuote(null)}
          title={`Devis #${viewQuote.id}`}
          size="large"
        >
          <div>
            <h3>Informations du client</h3>
            <p><strong>Nom :</strong> {viewQuote.client_name}</p>
            <p><strong>Email :</strong> {viewQuote.client_email}</p>
            <p><strong>Téléphone :</strong> {viewQuote.client_phone}</p>

            {viewQuote.notes && (
              <>
                <h3>Notes</h3>
                <p>{viewQuote.notes}</p>
              </>
            )}

            <h3>Articles</h3>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Quantité</th>
                  <th>Prix unitaire</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {viewQuote.items?.map((item) => (
                  <tr key={item.id}>
                    <td>{item.product?.name || 'N/A'}</td>
                    <td>{item.quantity}</td>
                    <td>{parseFloat(item.unit_price).toFixed(2)} DH</td>
                    <td>{(item.quantity * parseFloat(item.unit_price)).toFixed(2)} DH</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ textAlign: 'right', marginTop: '20px', fontSize: '18px', fontWeight: 'bold' }}>
              Total : {parseFloat(viewQuote.total).toFixed(2)} DH
            </div>
          </div>
        </Modal>
      )}

      {/* Convert to Sale Modal */}
      <Modal
        isOpen={showConvertModal}
        onClose={() => {
          setShowConvertModal(false);
          setConvertingQuote(null);
          setPaymentMethod('cash');
        }}
        title="Convertir le devis en vente"
      >
        <div>
          <p style={{ marginBottom: '15px' }}>
            Vous êtes sur le point de convertir le devis <strong>#{convertingQuote?.id}</strong> en vente.
            Cette action créera une nouvelle commande.
          </p>
          <div className="form-group">
            <label>Méthode de paiement *</label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="form-control"
            >
              <option value="cash">Espèces</option>
              <option value="card">Carte</option>
              <option value="transfer">Virement</option>
            </select>
          </div>
          {convertingQuote && (
            <div style={{
              padding: '15px',
              backgroundColor: '#f7fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              marginTop: '15px'
            }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#4a5568' }}>
                <strong>Client:</strong> {convertingQuote.client_name}
              </p>
              <p style={{ margin: '0', fontSize: '14px', color: '#4a5568' }}>
                <strong>Total:</strong> {parseFloat(convertingQuote.total).toFixed(2)} DH
              </p>
            </div>
          )}
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button
              onClick={confirmConvertToSale}
              className="btn btn-primary"
              disabled={converting}
            >
              <CreditCard size={18} />
              {converting ? 'Conversion...' : 'Confirmer la conversion'}
            </button>
            <button
              onClick={() => {
                setShowConvertModal(false);
                setConvertingQuote(null);
                setPaymentMethod('cash');
              }}
              className="btn btn-secondary"
              disabled={converting}
            >
              Annuler
            </button>
          </div>
        </div>
      </Modal>

      {/* Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          setConvertedSale(null);
        }}
        title="Conversion réussie"
      >
        <div>
          <div style={{
            padding: '20px',
            backgroundColor: '#f0fdf4',
            border: '1px solid #86efac',
            borderRadius: '4px',
            marginBottom: '15px',
            textAlign: 'center'
          }}>
            <ShoppingCart size={48} style={{ color: '#22c55e', marginBottom: '10px' }} />
            <p style={{ margin: '0', fontSize: '18px', fontWeight: 'bold', color: '#15803d' }}>
              Le devis a été converti en vente avec succès !
            </p>
          </div>
          {convertedSale && (
            <div style={{
              padding: '15px',
              backgroundColor: '#f7fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '4px'
            }}>
              <h4 style={{ marginTop: '0', marginBottom: '10px', color: '#2d3748' }}>Détails de la vente</h4>
              <p style={{ margin: '5px 0', fontSize: '14px' }}>
                <strong>ID de la commande:</strong> #{convertedSale.order?.id || 'N/A'}
              </p>
              <p style={{ margin: '5px 0', fontSize: '14px' }}>
                <strong>Numéro de facture:</strong> {convertedSale.order?.invoice_number || 'N/A'}
              </p>
              <p style={{ margin: '5px 0', fontSize: '14px' }}>
                <strong>Total:</strong> {convertedSale.order ? parseFloat(convertedSale.order.total).toFixed(2) : '0.00'} DH
              </p>
              <p style={{ margin: '5px 0', fontSize: '14px' }}>
                <strong>Méthode de paiement:</strong> {
                  convertedSale.order?.payment_method === 'cash' ? 'Espèces' :
                  convertedSale.order?.payment_method === 'card' ? 'Carte' :
                  convertedSale.order?.payment_method === 'transfer' ? 'Virement' :
                  'N/A'
                }
              </p>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
            <button
              onClick={() => {
                setShowSuccessModal(false);
                setConvertedSale(null);
              }}
              className="btn btn-primary"
            >
              Fermer
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default Quotes;
